import { useId, useRef, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import { useDatePickerState } from '@react-stately/datepicker';
import { useDatePicker, useDateSegment } from '@react-aria/datepicker';
import { useDateFieldState } from '@react-stately/datepicker';
import { useDateField } from '@react-aria/datepicker';
import { useLocale } from '@react-aria/i18n';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { useButton } from '@react-aria/button';
import { parseDate, createCalendar, today, getLocalTimeZone } from '@internationalized/date';
import type { CalendarDate, DateValue } from '@internationalized/date';
import type { DateFieldState, DateSegment } from '@react-stately/datepicker';
import type { InputSize } from '@components/ui/Input/TextInput';
import FormField from '@components/ui/Input/FormField';
import Calendar from './Calendar';
import styles from './DatePicker.module.scss';
import inputStyles from '@components/ui/Input/Input.module.scss';

/** Popover placement relative to the trigger input */
export type DatePickerPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

// ============================================
// TYPES
// ============================================

/** Props for the DatePicker component. */
export interface DatePickerProps {
  /** Selected date as ISO string: 'YYYY-MM-DD' */
  value?: string;
  /** Called when the date changes, receives ISO string 'YYYY-MM-DD' (or '' when the user clears the value). */
  onChange?: (value: string) => void;
  /** Label text displayed above the input */
  label?: string;
  /** Show required asterisk on the label */
  isRequired?: boolean;
  /** Help text below the input (hidden when error is present) */
  helperText?: string;
  /** Error message — replaces helperText, applies error border */
  error?: string;
  /** Placeholder text (used for aria, segments show placeholders natively) */
  placeholder?: string;
  /** Input size variant */
  size?: InputSize;
  /** Stretch to fill parent width */
  fullWidth?: boolean;
  /** Whether the date picker is disabled */
  disabled?: boolean;
  /** Minimum selectable date as ISO string: 'YYYY-MM-DD' */
  minDate?: string;
  /** Maximum selectable date as ISO string: 'YYYY-MM-DD' */
  maxDate?: string;
  /** Function to determine if a date is unavailable, receives ISO string */
  isDateUnavailable?: (dateIso: string) => boolean;
  /** Popover placement relative to the input */
  placement?: DatePickerPlacement;
  /** Locale for date segment order (e.g., 'en-GB' for dd/mm/yyyy). Defaults to browser locale. */
  locale?: string;
  /** Additional className on the outer wrapper */
  className?: string;
  /** Override auto-generated ID */
  id?: string;
  /** Forwarded ref to the field group element */
  ref?: React.Ref<HTMLDivElement>;
}

// ============================================
// HELPERS — ISO string <-> CalendarDate
// ============================================

function toCalendarDate(iso: string | undefined): CalendarDate | undefined {
  if (!iso) return undefined;
  try {
    return parseDate(iso);
  } catch {
    return undefined;
  }
}

// ============================================
// CALENDAR ICON
// ============================================

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M5 1v2M11 1v2M1.5 6h13M2.5 3h11a1 1 0 011 1v10a1 1 0 01-1 1h-11a1 1 0 01-1-1V4a1 1 0 011-1z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ============================================
// DATE PICKER COMPONENT
// ============================================

/**
 * A date picker component with a segment-based date input and calendar popover.
 * Accepts and emits ISO date strings ('YYYY-MM-DD') for easy API/form integration.
 * Built on react-aria for full accessibility and keyboard navigation.
 */
function DatePicker({
  ref,
  value,
  onChange,
  label,
  isRequired = false,
  helperText,
  error,
  placeholder: _placeholder,
  size = 'md',
  fullWidth = true,
  disabled = false,
  minDate,
  maxDate,
  isDateUnavailable,
  placement = 'bottom-start',
  locale: localeProp,
  className,
  id: idProp,
}: DatePickerProps) {
  const { t } = useTranslation();
  const autoId = useId();
  const id = idProp ?? autoId;
  const hasError = Boolean(error);

  const parsedValue = useMemo(() => toCalendarDate(value) ?? null, [value]);
  const parsedMinDate = useMemo(() => toCalendarDate(minDate), [minDate]);
  const parsedMaxDate = useMemo(() => toCalendarDate(maxDate), [maxDate]);

  const ariaIsDateUnavailable = useMemo(() => {
    if (!isDateUnavailable) return undefined;
    return (date: DateValue) => {
      const iso = date.toString();
      return isDateUnavailable(iso);
    };
  }, [isDateUnavailable]);

  const handleChange = useCallback(
    (date: DateValue | null) => {
      if (!onChange) return;
      // Emit empty string on clear so consumers see the cleared state —
      // matches the native <input type="date"> contract which fires onChange('').
      onChange(date ? date.toString() : '');
    },
    [onChange],
  );

  const state = useDatePickerState({
    value: parsedValue,
    onChange: handleChange,
    minValue: parsedMinDate,
    maxValue: parsedMaxDate,
    isDateUnavailable: ariaIsDateUnavailable,
    isDisabled: disabled,
    granularity: 'day',
    shouldCloseOnSelect: true,
  });

  const groupRef = useRef<HTMLDivElement>(null);
  const { groupProps, fieldProps, buttonProps, dialogProps } = useDatePicker(
    {
      label: label ?? 'Date',
      value: parsedValue,
      onChange: handleChange,
      minValue: parsedMinDate,
      maxValue: parsedMaxDate,
      isDateUnavailable: ariaIsDateUnavailable,
      isDisabled: disabled,
      isRequired,
      granularity: 'day',
      'aria-label': label ?? 'Date',
    },
    state,
    groupRef,
  );

  const describedBy = hasError
    ? `${id}-error`
    : helperText
      ? `${id}-helper`
      : undefined;

  const sizeClass = inputStyles[size] ?? inputStyles.md;

  const fieldClassName = [sizeClass, fullWidth && inputStyles.fullWidth, className]
    .filter(Boolean)
    .join(' ');

  const inputWrapperClasses = [
    styles.dateInput,
    inputStyles.inputBase,
    hasError && styles.dateInputError,
    disabled && styles.dateInputDisabled,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
      className={fieldClassName}
    >
      <div className={styles.inputWrapper}>
        <div {...groupProps} ref={groupRef} className={styles.inputGroup}>
          <DateField
            {...fieldProps}
            ref={ref}
            inputClassName={inputWrapperClasses}
            describedBy={describedBy}
            localeOverride={localeProp}
          />
          <CalendarButton {...buttonProps} isDisabled={disabled} />
        </div>

        {state.isOpen && (
          <CalendarPopover
            dialogProps={dialogProps}
            onClose={() => state.setOpen(false)}
            placement={placement}
          >
            <Calendar
              value={parsedValue}
              onChange={(date) => {
                handleChange(date);
                state.setOpen(false);
              }}
              minValue={parsedMinDate}
              maxValue={parsedMaxDate}
              isDateUnavailable={
                isDateUnavailable
                  ? (date) => isDateUnavailable(date.toString())
                  : undefined
              }
            />
            <div className={styles.calendarFooter}>
              <button
                type="button"
                className={styles.todayButton}
                onClick={() => {
                  const todayDate = today(getLocalTimeZone());
                  handleChange(todayDate);
                  state.setOpen(false);
                }}
              >
                {t('K_BUTTON_TODAY', 'Today')}
              </button>
            </div>
          </CalendarPopover>
        )}
      </div>
    </FormField>
  );
}

// ============================================
// DATE FIELD — segment-based input
// ============================================

interface DateFieldInternalProps {
  ref?: React.Ref<HTMLDivElement>;
  inputClassName?: string;
  describedBy?: string;
}

function DateField({
  ref,
  inputClassName,
  describedBy,
  localeOverride,
  ...props
}: DateFieldInternalProps & { localeOverride?: string } & Record<string, unknown>) {
  const { locale: browserLocale } = useLocale();
  const locale = localeOverride ?? browserLocale;
  const fieldRef = useRef<HTMLDivElement>(null);

  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const { fieldProps } = useDateField(
    { ...props, 'aria-describedby': describedBy },
    state,
    fieldRef,
  );

  // Merge refs
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      (fieldRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [ref],
  );

  return (
    <div className={inputClassName}>
      <div {...fieldProps} ref={setRefs} className={styles.segmentsWrapper}>
        {state.segments.map((segment, i) => (
          <DateSegmentView key={i} segment={segment} state={state} />
        ))}
      </div>
    </div>
  );
}

// ============================================
// DATE SEGMENT — individual editable segment
// ============================================

interface DateSegmentViewProps {
  segment: DateSegment;
  state: DateFieldState;
}

function DateSegmentView({ segment, state }: DateSegmentViewProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);

  const isLiteral = segment.type === 'literal';

  return (
    <span
      {...segmentProps}
      ref={ref}
      className={isLiteral ? styles.separator : styles.segment}
      data-placeholder={segment.isPlaceholder || undefined}
    >
      {segment.text}
    </span>
  );
}

// ============================================
// CALENDAR BUTTON — trigger for popover
// ============================================

interface CalendarButtonInternalProps {
  isDisabled?: boolean;
}

function CalendarButton({
  isDisabled,
  ...props
}: CalendarButtonInternalProps & Record<string, unknown>) {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(
    { ...props, isDisabled } as Parameters<typeof useButton>[0],
    ref,
  );

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={styles.calendarButton}
      disabled={isDisabled}
      tabIndex={-1}
    >
      <CalendarIcon />
    </button>
  );
}

// ============================================
// CALENDAR POPOVER — dropdown container
// ============================================

interface CalendarPopoverProps {
  dialogProps: ReturnType<typeof useDatePicker>['dialogProps'];
  children: React.ReactNode;
  onClose: () => void;
  placement?: DatePickerPlacement;
}

function CalendarPopover({ dialogProps, children, onClose, placement = 'bottom-start' }: CalendarPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { dialogProps: ariaDialogProps } = useDialog(dialogProps, popoverRef);

  // Close on outside click
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const placementClass = styles[`popover--${placement}`] ?? '';
  const popoverClasses = [styles.popover, placementClass].filter(Boolean).join(' ');

  return (
    <FocusScope restoreFocus>
      <div {...ariaDialogProps} ref={popoverRef} className={popoverClasses}>
        {children}
      </div>
    </FocusScope>
  );
}

export default DatePicker;
